import { useCallback, useEffect, useState } from "react";
import { useStateMachine } from "little-state-machine";
import { Button, Form } from "react-bootstrap";
import FocusTrap from "focus-trap-react";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import { enGB } from "date-fns/locale";
import { Divider } from "@whitespace/whitespace-ui";
import IDrawer from "interfaces/IDrawer";
import IUserHistoryFilter from "interfaces/IUserHistoryFilter";
import { updateIsUserHistoryFilterDrawerOpen } from "actions/updateIsUserHistoryFilterDrawerOpen";
import { updateUserHistoryFilter } from "actions/updateUserHistoryFilter";
import { initialUserHistoryFilter } from "modules/InitialUserHistoryFilter";
import DatePicker, { registerLocale } from "react-datepicker";
import SitesFilter from "components/FilterSections/SitesFilter";
import ActionFilter from "./ActionFilter";
import "styles/form.scss";
import "styles/form-mixins.scss";
import "react-datepicker/dist/react-datepicker.css";

const UserHistoryFilterDrawer = ({ isOpen, onClose }: IDrawer) => {
  const { actions } = useStateMachine({
    updateUserHistoryFilter,
    updateIsUserHistoryFilterDrawerOpen
  });

  const { state } = useStateMachine();
  const [hasActionFilterChanged, setHasActionFilterChanged] = useState(false);
  const [hasSiteFilterChanged, setHasSiteFilterChanged] = useState(false);
  const [accordionKey, setAccordionKey] = useState(0);
  const [applyFilterButtonDisabled, setApplyFilterButtonIsDisabled] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date>();

  registerLocale("enGb", enGB);

  const {
    formState: { errors }, register, handleSubmit, getValues, setValue
  } = useForm({
    shouldFocusError: true,
    mode: "onSubmit",
    reValidateMode: "onBlur"
  });

  const constructFilterFromForm = useCallback(() => {
    const userHistoryFilter : IUserHistoryFilter = initialUserHistoryFilter();
    userHistoryFilter.searchText = state.userHistoryFilter!.searchText;
    if (getValues("logDate")) {
      userHistoryFilter.logDate = format(new Date(getValues("logDate")), "yyyy-MM-dd", { locale: enGB });
    }
    const actionsSelected = getValues("actions") as string[];
    if (actionsSelected) {
      if (typeof actionsSelected === "string") {
        userHistoryFilter.actions.push(actionsSelected);
      } else {
        actionsSelected.forEach(action => {
          userHistoryFilter.actions.push(action);
        });
      }
    }
    const sitesSelected = getValues("sites") as string[];

    if (sitesSelected) {
      if (typeof sitesSelected === "string") {
        userHistoryFilter.sites.push(sitesSelected);
      } else {
        sitesSelected.forEach(site => {
          userHistoryFilter.sites.push(site);
        });
      }
    }

    return userHistoryFilter;
  }, [getValues]);

  const handleSelectionChange = () => {
    const filter = constructFilterFromForm();
    setApplyFilterButtonIsDisabled(!(filter.actions.length > 0 || filter.sites.length > 0 || filter.logDate !== undefined));
  };

  const handleApplyFilterClick = () => {
    const userHistoryFilter = constructFilterFromForm();
    userHistoryFilter.searchText = state.userHistoryFilter!.searchText;
    actions.updateUserHistoryFilter(userHistoryFilter);
    onClose();
  };

  const handleResetClick = () => {
    const userHistoryFilter = { ...state.userHistoryFilter } as IUserHistoryFilter;
    setSelectedDate(undefined);
    userHistoryFilter.logDate = undefined;
    userHistoryFilter.actions = [];
    userHistoryFilter.sites = [];
    actions.updateUserHistoryFilter(userHistoryFilter);
    onClose();
  };

  const className = `offcanvas offcanvas-end ${isOpen ? "show" : ""}`;
  const backdropClassName = `backdrop ${!isOpen ? "hidden" : ""}`;
  const tabIndex = isOpen ? 0 : -1;

  const setControlValues = useCallback((userHistoryFilter : IUserHistoryFilter) => {
    if (state.isUserHistoryFilterDrawerOpen) {
      setValue("logDate", userHistoryFilter.logDate ? format(new Date(userHistoryFilter.logDate), "yyyy-MM-dd", { locale: enGB }) : "");
      setSelectedDate(userHistoryFilter.logDate ? new Date(userHistoryFilter.logDate) : undefined);

      setValue("actions", userHistoryFilter.actions.length > 0 ? userHistoryFilter.actions.map(String) : "");
      setValue("sites", userHistoryFilter.sites.length > 0 ? userHistoryFilter.sites.map(String) : "");
    }
  }, [setValue, state.isUserHistoryFilterDrawerOpen]);

  useEffect(() => {
    if (state.isUserHistoryFilterDrawerOpen && state.userHistoryFilter) {
      setAccordionKey(new Date().getTime());
      setHasActionFilterChanged(state.userHistoryFilter !== undefined && state.userHistoryFilter.actions.length > 0);
      setHasSiteFilterChanged(state.userHistoryFilter !== undefined && state.userHistoryFilter.sites.length > 0);
      setControlValues(state.userHistoryFilter!);
    }
  }, [state.isUserHistoryFilterDrawerOpen, state.userHistoryFilter]);

  useEffect(() => {
    setControlValues(state.userHistoryFilter!);
    setApplyFilterButtonIsDisabled(!(state.userHistoryFilter!.actions.length > 0 || state.userHistoryFilter!.sites.length > 0
      || state.userHistoryFilter!.logDate !== undefined));
  }, [setControlValues]);

  // Awaiting Refactor for accessibility.
  /* eslint-disable jsx-a11y/click-events-have-key-events */
  /* eslint-disable jsx-a11y/no-static-element-interactions */
  return (
    <FocusTrap active={process.env.REACT_APP_AMPLIFY_TEST_MODE === "true" ? false : isOpen}>
      <div className="filter-drawer" data-testid="userHistoryDrawer">
        <div className={backdropClassName} onClick={onClose} />

        <Form noValidate onSubmit={handleSubmit(handleApplyFilterClick)}>
          <div className={className} id="offcanvasRight">
            <section>
              <div className="d-flex m-3">
                <h2 className="w-100 m-0" data-testid="filter-header">User History Filter</h2>
                <Button type="button" className="btn-close" data-testid="btnUserFilterClose" onClick={onClose} tabIndex={tabIndex} aria-label="Close" />
              </div>
              <Divider />
              <div className="d-flex justify-content-space-between align-content-center">
                <div className="m-3">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={applyFilterButtonDisabled}
                    tabIndex={tabIndex}
                    style={{ marginRight: "1em" }}
                  >
                    Apply Filter
                  </Button>
                  <Button data-testid="reset-button" variant="secondary" onClick={handleResetClick} tabIndex={tabIndex}>Reset</Button>
                </div>
              </div>
            </section>
            <Divider />
            <section className="m-3">
              <Form.Group controlId="logDate">
                <Form.Label id="userFilterDate" className="horiz-form-label">Date</Form.Label>
                {state.userHistoryFilterDates && state.userHistoryFilterDates.length > 0 && (
                  <DatePicker
                    selected={selectedDate}
                    onChange={(date:Date) => { setValue("logDate", date); setSelectedDate(date); handleSelectionChange(); }}
                    includeDates={state.userHistoryFilterDates ? state.userHistoryFilterDates.map(d => new Date(d)) : []}
                    locale="enGb"
                    dateFormat="dd/MM/yyyy"
                    isClearable
                    placeholderText="Select a date to filter by"
                    aria-required="false"
                    aria-invalid="false"
                    aria-label="User History Filtered Date"
                    ariaLabelledBy="userFilterDate"
                    closeOnScroll
                  />
                ) }
                {(!state.userHistoryFilterDates || state.userHistoryFilterDates.length === 0)
                && <p>No selectable dates</p>}
              </Form.Group>
              {errors && errors.logDate && <div className="validation-error" data-testid="date-validation-error">{errors.logDate.message}</div>}
            </section>
            {isOpen
            && (
              <>
                <Divider />
                <ActionFilter
                  accordionKey={`action_${accordionKey}`}
                  actions={state.actionsFiltered!}
                  register={register}
                  isUserHistoryFilterDrawerOpen={state.isUserHistoryFilterDrawerOpen}
                  hasFilterChanged={hasActionFilterChanged}
                  onSelectionChange={handleSelectionChange}

                />
                <Divider />
                <SitesFilter
                  accordionKey={`site_${accordionKey}`}
                  register={register}
                  sites={state.sitesFiltered!}
                  hasFilterChanged={hasSiteFilterChanged}
                  onSelectionChange={handleSelectionChange}
                />
                <Divider />
              </>
            )}
          </div>
        </Form>
      </div>
    </FocusTrap>
  );
};
export default UserHistoryFilterDrawer;
